'use client';

import { useState, useRef, useCallback, useEffect } from 'react';
import { Smile, SendHorizonal, Loader2, X } from 'lucide-react';
import { cn, truncateAddress } from '@/lib/utils';
import { useWallet } from '@/hooks/use-wallet';
import { useChatDraft } from '@/contexts/chat-draft-context';
import data from '@emoji-mart/data';
import Picker from '@emoji-mart/react';

export interface ReplyPreview {
  id: number;
  message: string;
  display_name: string | null;
  address?: string;
}

interface MentionSuggestion {
  address: string;
  displayName: string | null;
  avatarUrl: string | null;
}

interface ChatInputProps {
  onSend: (message: string, replyToId?: number) => Promise<void>;
  onTyping?: () => void;
  disabled?: boolean;
  cooldownUntil?: number | null;
  replyTo?: ReplyPreview | null;
  onCancelReply?: () => void;
  room?: string;
}

function useMentionAutocomplete(text: string, cursorPos: number, room?: string, excludeAddress?: string) {
  const [suggestions, setSuggestions] = useState<MentionSuggestion[]>([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [mentionQuery, setMentionQuery] = useState<string | null>(null);
  const [mentionStart, setMentionStart] = useState(-1);
  const debounceRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const before = text.slice(0, cursorPos);
    const atMatch = before.match(/@(\w*)$/);

    if (!atMatch) {
      setMentionQuery(null);
      setSuggestions([]);
      setActiveIndex(0);
      return;
    }

    const query = atMatch[1];
    setMentionStart(cursorPos - query.length - 1);
    setMentionQuery(query);

    if (query.length === 0) {
      setSuggestions([]);
      return;
    }

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const roomParam = room ? `&room=${encodeURIComponent(room)}` : '';
        const res = await fetch(`/api/users/search?q=${encodeURIComponent(query)}&limit=3${roomParam}`);
        if (!res.ok) return;
        const json = await res.json();
        const users: MentionSuggestion[] = json.users ?? [];
        const filtered = excludeAddress
          ? users.filter((u) => u.address.toLowerCase() !== excludeAddress.toLowerCase())
          : users;
        setSuggestions(filtered);
        setActiveIndex(0);
      } catch {
        setSuggestions([]);
      }
    }, 200);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [text, cursorPos, room]);

  const isOpen = mentionQuery !== null && suggestions.length > 0;

  return { suggestions, activeIndex, setActiveIndex, isOpen, mentionStart, mentionQuery };
}

export function ChatInput({ onSend, onTyping, disabled, cooldownUntil, replyTo, onCancelReply, room }: ChatInputProps) {
  const { address } = useWallet();
  // Source draft state from `ChatDraftContext` so an in-progress
  // composition survives every common unmount path: the desktop chat
  // sheet closing, the mobile chip-rail flipping back to the canvas,
  // navigating between `/chat` and `/board/...`, board-switching, and
  // even a hard refresh (the provider mirrors to `sessionStorage`).
  // Keyed by `room` so each board's draft is isolated. Falls back to
  // `'boardroom'` to match `ChatPanel`'s default — keeps deep-linked
  // panel surfaces (tutorial, action-replay) working when the host
  // doesn't pipe a room id through.
  const draftRoom = room ?? 'boardroom';
  const { draft, setDraft, clearDraft } = useChatDraft(draftRoom);
  const text = draft.text;
  const cursorPos = draft.cursor;
  const setText = useCallback(
    (next: string | ((prev: string) => string)) => {
      const value = typeof next === 'function' ? next(draft.text) : next;
      setDraft({ text: value });
    },
    [draft.text, setDraft],
  );
  const setCursorPos = useCallback(
    (next: number | ((prev: number) => number)) => {
      const value = typeof next === 'function' ? next(draft.cursor) : next;
      setDraft({ cursor: value });
    },
    [draft.cursor, setDraft],
  );
  const [sending, setSending] = useState(false);
  const [showEmoji, setShowEmoji] = useState(false);
  const [cooldownLeft, setCooldownLeft] = useState('');
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const emojiRef = useRef<HTMLDivElement>(null);
  const typingTimerRef = useRef<NodeJS.Timeout | null>(null);
  // Mention map mirrors `draft.mentions` but keeps a `Map` shape for
  // the existing iteration code (`for (const [k, v] of map)`) to read
  // without rewriting. We rehydrate from the draft on every render so
  // a session-restored draft repopulates the map on first paint.
  const mentionMapRef = useRef<Map<string, string>>(new Map());
  // Sync the ref's contents with the draft's `mentions` record. We
  // preserve the ref identity (not reassigning) so the same `Map`
  // instance survives across renders — the textarea-change handler
  // and the autocomplete completion path both mutate it directly.
  if (
    mentionMapRef.current.size !== Object.keys(draft.mentions).length ||
    Array.from(mentionMapRef.current.entries()).some(
      ([k, v]) => draft.mentions[k] !== v,
    )
  ) {
    mentionMapRef.current.clear();
    for (const [k, v] of Object.entries(draft.mentions)) {
      mentionMapRef.current.set(k, v);
    }
  }

  const mention = useMentionAutocomplete(text, cursorPos, room, address ?? undefined);

  const completeMention = useCallback((user: MentionSuggestion, atIdx: number) => {
    const el = inputRef.current;
    const currentText = el?.value ?? text;
    const currentCursor = el?.selectionStart ?? cursorPos;

    if (atIdx < 0 || atIdx >= currentText.length) return;

    const prefix = currentText.slice(0, atIdx);
    const after = currentText.slice(currentCursor);
    const rawLabel = user.displayName || truncateAddress(user.address);
    const displayLabel = rawLabel.startsWith('@') ? rawLabel.slice(1) : rawLabel;
    const insertion = `@${displayLabel} `;
    const newText = prefix + insertion + after;

    mentionMapRef.current.set(displayLabel, user.address);

    const truncatedText = newText.slice(0, 500);
    const newCursor = prefix.length + insertion.length;
    // Persist text + cursor + mentions in a single draft patch so a
    // mid-composition unmount leaves all three in sync. Without the
    // mentions snapshot, a session-restored draft would re-tokenise
    // `@alice` as plain text on send (no entry in the lookup → the
    // `replaceAll` below leaves the literal `@alice` in the message).
    setDraft({
      text: truncatedText,
      cursor: newCursor,
      mentions: Object.fromEntries(mentionMapRef.current),
    });

    requestAnimationFrame(() => {
      if (el) {
        el.focus();
        el.setSelectionRange(newCursor, newCursor);
      }
    });
  }, [text, cursorPos, setDraft]);

  // Cooldown countdown
  useEffect(() => {
    if (!cooldownUntil || cooldownUntil <= Date.now()) {
      setCooldownLeft('');
      return;
    }
    const tick = () => {
      const remaining = cooldownUntil - Date.now();
      if (remaining <= 0) {
        setCooldownLeft('');
        return;
      }
      const hours = Math.floor(remaining / 3_600_000);
      const mins = Math.floor((remaining % 3_600_000) / 60_000);
      setCooldownLeft(`${hours}h ${mins}m`);
    };
    tick();
    const interval = setInterval(tick, 60_000);
    return () => clearInterval(interval);
  }, [cooldownUntil]);

  // Close emoji picker on click outside
  useEffect(() => {
    if (!showEmoji) return;
    const handler = (e: MouseEvent) => {
      if (emojiRef.current && !emojiRef.current.contains(e.target as Node)) {
        setShowEmoji(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showEmoji]);

  const handleSend = useCallback(async () => {
    const trimmed = text.trim();
    if (!trimmed || sending || disabled) return;

    let messageToSend = trimmed;
    for (const [displayLabel, address] of mentionMapRef.current) {
      messageToSend = messageToSend.replaceAll(`@${displayLabel}`, `@[${address}]`);
    }

    setSending(true);
    try {
      await onSend(messageToSend, replyTo?.id);
      // Single `clearDraft` wipes text, cursor, replyTo, and mentions
      // in one shot — both in-memory and in `sessionStorage` (via the
      // provider). Mirrors the historic three-way reset
      // (`setText('')` / `setCursorPos(0)` / `mentionMapRef.clear()`)
      // but adds the persistent layer's clear so a refresh after send
      // doesn't repaint the just-sent message into the input.
      mentionMapRef.current.clear();
      clearDraft();
      onCancelReply?.();
      inputRef.current?.focus();
    } finally {
      setSending(false);
    }
  }, [text, sending, disabled, onSend, replyTo, onCancelReply, clearDraft]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (mention.isOpen) {
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          mention.setActiveIndex((i) => Math.min(i + 1, mention.suggestions.length - 1));
          return;
        }
        if (e.key === 'ArrowUp') {
          e.preventDefault();
          mention.setActiveIndex((i) => Math.max(i - 1, 0));
          return;
        }
        if (e.key === 'Tab' || e.key === 'Enter') {
          e.preventDefault();
          completeMention(mention.suggestions[mention.activeIndex], mention.mentionStart);
          return;
        }
        if (e.key === 'Escape') {
          e.preventDefault();
          mention.setActiveIndex(0);
          return;
        }
      }

      if (e.key === 'Enter') {
        // Skip while an IME is composing — the Enter keystroke is
        // committing the candidate, not submitting the message.
        if (e.nativeEvent.isComposing) return;
        // Shift/Cmd/Ctrl + Enter inserts a newline (default textarea
        // behaviour); plain Enter sends.
        if (e.shiftKey || e.metaKey || e.ctrlKey) return;
        e.preventDefault();
        handleSend();
      }
    },
    [handleSend, mention, completeMention]
  );

  const handleEmojiSelect = useCallback((emoji: { native: string }) => {
    setText((prev) => prev + emoji.native);
    setShowEmoji(false);
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    if (replyTo) inputRef.current?.focus();
  }, [replyTo]);

  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = 'auto';
    const h = Math.min(el.scrollHeight, 100);
    el.style.height = h + 'px';
    el.style.overflow = el.scrollHeight > 100 ? 'auto' : 'hidden';
  }, [text]);

  const isCoolingDown = Boolean(cooldownUntil && cooldownUntil > Date.now());
  const isDisabled = disabled || isCoolingDown;

  return (
    <div className="border-t border-border bg-background px-3 py-2">
      {isCoolingDown && (
        <div className="text-xs text-amber-500 mb-1.5 px-1">
          You are muted for {cooldownLeft}
        </div>
      )}
      {replyTo && (
        <div className="flex items-center gap-2 mb-1.5 px-1 py-1 rounded-lg bg-muted/30 border border-border/60">
          <div className="flex-1 min-w-0 px-1.5">
            <span className="text-[11px] font-medium text-muted-foreground">
              Replying to {replyTo.display_name || (replyTo.address ? truncateAddress(replyTo.address) : 'user')}
            </span>
            <p className="text-[11px] text-muted-foreground/70 truncate">
              {replyTo.message.length > 80 ? replyTo.message.slice(0, 80) + '...' : replyTo.message}
            </p>
          </div>
          <button
            onClick={onCancelReply}
            className="p-1 rounded text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors shrink-0"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      )}

      {/* Mention autocomplete dropdown */}
      {mention.isOpen && (
        <div className="mb-1.5 rounded-lg border border-border bg-popover shadow-lg overflow-hidden">
          {mention.suggestions.map((user, i) => (
            <button
              key={user.address}
              type="button"
              onMouseDown={(e) => {
                e.preventDefault();
                completeMention(user, mention.mentionStart);
              }}
              className={cn(
                "flex items-center gap-2 w-full px-3 py-2 text-left text-sm transition-colors",
                i === mention.activeIndex ? "bg-muted" : "hover:bg-muted/50",
              )}
            >
              {user.avatarUrl ? (
                <img src={user.avatarUrl} alt="" className="h-5 w-5 rounded-full shrink-0" />
              ) : (
                <div className="h-5 w-5 rounded-full bg-muted shrink-0" />
              )}
              <span className="font-medium truncate">
                {user.displayName || truncateAddress(user.address)}
              </span>
              <span className="text-xs text-muted-foreground shrink-0">
                {truncateAddress(user.address)}
              </span>
            </button>
          ))}
        </div>
      )}

      <div className="flex items-end gap-2">
        {/* Emoji button */}
        <div className="relative" ref={emojiRef}>
          <button
            type="button"
            onClick={() => setShowEmoji((v) => !v)}
            disabled={isDisabled}
            className={cn(
              'p-1.5 rounded-lg transition-colors',
              'text-muted-foreground hover:text-foreground hover:bg-muted/50',
              'disabled:opacity-40 disabled:cursor-not-allowed'
            )}
          >
            <Smile className="h-5 w-5" />
          </button>
          {showEmoji && (
            <div className="absolute bottom-10 left-0 z-50">
              <Picker
                data={data}
                onEmojiSelect={handleEmojiSelect}
                theme="dark"
                previewPosition="none"
                skinTonePosition="none"
                maxFrequentRows={1}
                perLine={8}
              />
            </div>
          )}
        </div>

        <textarea
          ref={inputRef}
          value={text}
          onChange={(e) => {
            setText(e.target.value.slice(0, 500));
            setCursorPos(e.target.selectionStart ?? e.target.value.length);
            if (onTyping && e.target.value.trim()) {
              if (!typingTimerRef.current) {
                onTyping();
                typingTimerRef.current = setTimeout(() => {
                  typingTimerRef.current = null;
                }, 3_000);
              }
            }
          }}
          onSelect={(e) => {
            setCursorPos((e.target as HTMLTextAreaElement).selectionStart ?? cursorPos);
          }}
          onKeyDown={handleKeyDown}
          disabled={isDisabled}
          placeholder={isCoolingDown ? 'You are muted' : 'Message... (@ to mention)'}
          rows={1}
          className={cn(
            'flex-1 resize-none bg-muted/30 border border-border rounded-xl px-3 py-2 text-sm',
            'placeholder:text-muted-foreground/50 focus:outline-none focus:ring-1',
            isCoolingDown
              ? 'focus:ring-destructive border-destructive/50'
              : 'focus:ring-amber-400/45 border-amber-500/15',
            'disabled:opacity-40 disabled:cursor-not-allowed',
            'max-h-[100px] min-h-[36px]'
          )}
          style={{ height: 'auto' }}
          onInput={(e) => {
            const el = e.currentTarget;
            el.style.height = 'auto';
            const h = Math.min(el.scrollHeight, 100);
            el.style.height = h + 'px';
            el.style.overflow = el.scrollHeight > 100 ? 'auto' : 'hidden';
          }}
        />

        {/* Send button */}
        <button
          type="button"
          onClick={handleSend}
          disabled={isDisabled || !text.trim() || sending}
          className={cn(
            'p-1.5 rounded-lg shrink-0 transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-400/45 focus-visible:ring-offset-2 focus-visible:ring-offset-background',
            !isDisabled && (text.trim() || sending)
              ? 'text-zinc-950 bg-gradient-to-b from-amber-400 to-amber-500 border border-amber-300/60 shadow-[0_2px_8px_-2px_rgba(251,191,36,0.45)] hover:brightness-110 hover:shadow-[0_4px_14px_-3px_rgba(251,191,36,0.55)] active:scale-[0.94] active:brightness-95 disabled:cursor-not-allowed'
              : 'text-muted-foreground/40 border border-transparent',
            sending && 'disabled:opacity-100'
          )}
        >
          {sending ? (
            <Loader2 className="h-5 w-5 animate-spin text-zinc-950" />
          ) : (
            <SendHorizonal className="h-5 w-5" />
          )}
        </button>
      </div>
    </div>
  );
}
